import { auth, db } from '@/firebase'
import { collection, getDocs } from 'firebase/firestore'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { loginFor } from '@/lib/panelRoutes'

export const PANEL_SESSION_KEYS = [
  'role',
  'city',
  'officeName',
  'entityType',
  'entityId',
  'businessDisplayName',
  'deliveryByOffice',
  'hallOrders',
  'lmenus',
  'userType',
  'appTheme',
  'officeUsername',
  'officeCredVersion',
  'adminUsername',
  'adminCredVersion',
  'ownerAccountDocId',
  'posEnabled',
  'posRole',
  'posStaffId',
  'posStaffName',
  'restaurantName'
]

export function readCredVersion(city, kind) {
  const key = kind === 'admin' ? 'adminCredVersion' : 'officeCredVersion'
  const n = Number(city?.[key])
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1
}

/** @returns {Promise<object|null>} */
export async function findCityByNameEn(cityNameEn) {
  const key = String(cityNameEn || '').trim()
  if (!key) return null

  const snap = await getDocs(collection(db, 'regions'))
  for (const regionDoc of snap.docs) {
    const cities = regionDoc.data()?.cities
    if (!Array.isArray(cities)) continue
    const found = cities.find((c) => String(c?.name_en || '').trim() === key)
    if (found) return found
  }
  return null
}

export function clearPanelSession() {
  if (typeof localStorage === 'undefined') return
  const savedLang = localStorage.getItem('appLang')
  PANEL_SESSION_KEYS.forEach((k) => localStorage.removeItem(k))
  if (savedLang) localStorage.setItem('appLang', savedLang)
  if (auth.currentUser) signOut(auth).catch(() => {})
}

export function persistOfficeSession({ city, officeName, username, officeCredVersion }) {
  localStorage.setItem('role', 'office')
  localStorage.setItem('city', String(city || '').trim())
  localStorage.setItem('officeName', String(officeName || 'مكتب التوصيل').trim())
  localStorage.setItem('officeUsername', String(username || '').trim())
  localStorage.setItem('officeCredVersion', String(readCredVersion({ officeCredVersion }, 'office')))
}

export function persistAdminSession({ city, username, adminCredVersion }) {
  localStorage.setItem('role', 'admin')
  localStorage.setItem('city', String(city || '').trim())
  localStorage.setItem('adminUsername', String(username || '').trim())
  localStorage.setItem('adminCredVersion', String(readCredVersion({ adminCredVersion }, 'admin')))
}

export function persistBusinessOwnerSession(acc) {
  localStorage.setItem('role', 'business_owner')
  localStorage.setItem('city', String(acc.city || '').trim())
  localStorage.setItem('entityType', String(acc.entityType || '').trim())
  localStorage.setItem('entityId', String(acc.entityId || '').trim())
  localStorage.setItem('businessDisplayName', String(acc.displayName || '').trim())
  localStorage.setItem('ownerAccountDocId', String(acc.id || '').trim())
  localStorage.setItem('deliveryByOffice', acc.deliveryByOffice !== false ? 'true' : 'false')
  localStorage.setItem('hallOrders', acc.hallOrders === true ? 'true' : 'false')
  localStorage.setItem(
    'lmenus',
    acc.lmenus === true ||
      acc.lmenus === 'true' ||
      String(acc.city || '').trim() === 'virtual'
      ? 'true'
      : 'false'
  )
  localStorage.setItem('posEnabled', acc.posEnabled === true ? 'true' : 'false')
}

export function persistPosStaffSession(p) {
  localStorage.setItem('role', 'pos_staff')
  localStorage.setItem('posRole', String(p.posRole || ''))
  localStorage.setItem('city', String(p.city || '').trim())
  localStorage.setItem('entityType', 'restaurants')
  localStorage.setItem('entityId', String(p.entityId || '').trim())
  localStorage.setItem('posStaffId', String(p.posStaffId || ''))
  localStorage.setItem('posStaffName', String(p.displayName || ''))
  localStorage.setItem('restaurantName', String(p.restaurantName || ''))
  localStorage.setItem('lmenus', p.lmenus === true ? 'true' : 'false')
  localStorage.setItem('posEnabled', 'true')
}

let authReadyPromise = null
/** Resolves once Firebase Auth has restored (or not) the saved sign-in. */
export function authReady() {
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const stop = onAuthStateChanged(auth, (user) => {
        stop()
        resolve(user)
      })
    })
  }
  return authReadyPromise
}

// Refresh the ID token now and then so revoked accounts (password changed,
// account disabled or deleted) are signed out within a few minutes.
const FORCE_REFRESH_MS = 5 * 60 * 1000
let lastForcedRefresh = 0

/**
 * Checks the panel session against the Firebase Auth token claims
 * (set by the `panelLogin` Cloud Function).
 */
export async function validatePanelSession(role) {
  const r = String(role || '').trim()
  await authReady()
  const user = auth.currentUser
  if (!user) return { ok: false, reason: 'not_signed_in' }

  let result
  try {
    // Claims from the cached token: no network wait on every navigation.
    result = await user.getIdTokenResult(false)
  } catch (e) {
    const code = String(e?.code || '')
    if (code === 'auth/network-request-failed') return { ok: true, offline: true }
    return { ok: false, reason: 'token_revoked' }
  }
  // Every few minutes, refresh in the background so a revoked account
  // (password changed, disabled, deleted) is signed out soon after.
  if (Date.now() - lastForcedRefresh > FORCE_REFRESH_MS) {
    lastForcedRefresh = Date.now()
    user.getIdToken(true).catch((e) => {
      if (String(e?.code || '') === 'auth/network-request-failed') return
      const login = loginFor(r)
      clearPanelSession()
      if (typeof window !== 'undefined') window.location.href = login
    })
  }

  const c = result.claims || {}
  if (c.panel !== true || c.role !== r) return { ok: false, reason: 'role_mismatch' }
  if (r === 'business_owner' || r === 'pos_staff') {
    if (String(c.entityId || '') !== String(localStorage.getItem('entityId') || '')) {
      return { ok: false, reason: 'entity_mismatch' }
    }
  }
  if (r === 'pos_staff' && !c.posRole) return { ok: false, reason: 'no_pos_role' }
  if (r === 'pos_staff' && c.posRole !== localStorage.getItem('posRole')) {
    localStorage.setItem('posRole', String(c.posRole || ''))
  }
  if ((r === 'office' || r === 'admin') && String(c.city || '') !== String(localStorage.getItem('city') || '')) {
    return { ok: false, reason: 'city_mismatch' }
  }
  return { ok: true, claims: c }
}

export function officeCredentialsChanged(existing, next) {
  if (!existing || !next) return true
  return (
    String(existing.username || '').trim() !== String(next.username || '').trim() ||
    String(existing.password || '').trim() !== String(next.password || '').trim() ||
    String(existing.deliveryOfficeName || '').trim() !== String(next.deliveryOfficeName || '').trim()
  )
}

export function adminCredentialsChanged(existing, next) {
  if (!existing || !next) return true
  return (
    String(existing.username_admin || '').trim() !== String(next.username_admin || '').trim() ||
    String(existing.password_admin || '').trim() !== String(next.password_admin || '').trim()
  )
}

export function applyCredVersionsForCityWrite(existing, next) {
  const out = { ...next }
  if (officeCredentialsChanged(existing, next)) {
    out.officeCredVersion = readCredVersion(existing, 'office') + 1
  } else {
    out.officeCredVersion = readCredVersion(existing, 'office')
  }
  if (adminCredentialsChanged(existing, next)) {
    out.adminCredVersion = readCredVersion(existing, 'admin') + 1
  } else {
    out.adminCredVersion = readCredVersion(existing, 'admin')
  }
  return out
}

export function initialCredVersionsForNewCity(city) {
  return {
    ...city,
    officeCredVersion: 1,
    adminCredVersion: 1
  }
}
